/**
 * One contractor's AP, end to end — every commitment they hold, across every
 * project, and every bill billed against those commitments. The landing
 * Payables page groups its commitments table by contractor precisely so this
 * page can be the drill-down: the same rows, filtered to one contractor
 * instead of scanned across all of them.
 */
import { sub, sum } from "@dantria/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, fmtAmount as fmt, formatPercent, unwrap } from "../api";
import {
  Button,
  DeleteButton,
  Empty,
  ErrorNote,
  Loading,
  Panel,
  Stat,
  Table,
  TableSearch,
  Td,
  Th,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { ContractorPortalAccess, canManagePortalAccess } from "./Contractors";
import { PriceBookPanel } from "./estimating";
import { CommitmentEarnings, RecordPaymentForm, ScheduleRowsForm } from "./shared";

export function ContractorDetail({
  slug,
  contractorId,
  currency,
  role,
  onBack,
}: {
  slug: string;
  contractorId: string;
  currency: string;
  role: string;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const del = useDeleteRecord(slug);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [settlingBillId, setSettlingBillId] = useState<string | null>(null);

  // The contractor's own record — name, kind, terms — has no single-row
  // fetch, so it's found in the same list the landing page already reads.
  const contractors = useQuery({
    queryKey: ["contractors", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].contractors.$get({ param: { slug } })),
  });
  const contractor = contractors.data?.contractors.find((c) => c.id === contractorId);

  const commitments = useQuery({
    queryKey: ["commitments", slug, "contractor", contractorId],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].payables.commitments.$get({
          param: { slug },
          query: { contractorId },
        }),
      ),
  });
  const bills = useQuery({
    queryKey: ["bills", slug, "contractor", contractorId],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].payables.bills.$get({
          param: { slug },
          query: { contractorId },
        }),
      ),
  });
  const draws = useQuery({
    queryKey: ["draws", slug, openId],
    enabled: !!openId,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].payables.commitments[":id"].draws.$get({
          param: { slug, id: openId! },
        }),
      ),
  });

  function refresh() {
    for (const k of [
      "commitments",
      "bills",
      "cost-summary",
      "cost-summary-contractors",
      "draws",
      "commitment-earnings",
    ]) {
      qc.invalidateQueries({ queryKey: [k, slug] });
    }
  }

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  const pay = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setError(null),
    onSuccess: () => {
      setSettlingBillId(null);
      refresh();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  const commitmentsTable = useTable(commitments.data?.commitments ?? [], {
    filter: (cm, q) =>
      `${cm.projectName} ${cm.scope ?? ""}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      project: (cm) => cm.projectName.toLowerCase(),
      scope: (cm) => (cm.scope ?? "").toLowerCase(),
      retainage: (cm) => Number(cm.retainagePct),
      value: (cm) => Number(cm.contractValue),
      billed: (cm) => Number(cm.billed),
      stillToCome: (cm) => Number(sub(cm.contractValue, cm.billed)),
      outstanding: (cm) => Number(cm.outstanding),
    },
  });

  const drawScheduleTable = useTable([...(draws.data?.schedule.milestones ?? [])], {
    filter: (d, q) =>
      `${d.name} ${d.frozen ? "billed" : d.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      seq: (d) => d.seq,
      draw: (d) => d.name.toLowerCase(),
      status: (d) => (d.frozen ? "billed" : d.status),
      amount: (d) => Number(d.amount),
      outstanding: (d) => Number(draws.data?.settlements[d.id]?.outstanding ?? 0),
    },
  });

  const billsTable = useTable(bills.data?.bills ?? [], {
    filter: (b, q) =>
      `${b.projectName} ${b.number ?? ""} ${b.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      project: (b) => b.projectName.toLowerCase(),
      ref: (b) => (b.number ?? "").toLowerCase(),
      due: (b) => b.dueDate,
      status: (b) => b.status,
      retainage: (b) => Number(b.retainageWithheld),
      total: (b) => Number(b.total),
      paid: (b) => Number(b.amountPaid),
      outstanding: (b) => Number(b.outstanding),
    },
  });

  if (contractors.isPending || commitments.isPending) return <Loading />;

  const rows = commitments.data?.commitments ?? [];
  const committed = sum(rows.map((cm) => cm.contractValue));
  const billed = sum(rows.map((cm) => cm.billed));
  const outstanding = sum(rows.map((cm) => cm.outstanding));

  return (
    <>
      <nav className="sticky top-14 z-20 -mx-6 mb-4 border-b border-neutral-200 bg-neutral-50/90 px-6 pb-3 pt-3 backdrop-blur-md">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-base font-semibold tracking-tight">
              {contractor?.name ?? "Contractor"}
            </h1>
            {contractor && (
              <p className="text-xs text-neutral-500">
                {contractor.kind} · retainage {formatPercent(contractor.defaultRetainagePct)} ·
                terms {contractor.paymentTermsDays}d
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onBack}
            className="mt-0.5 whitespace-nowrap text-sm text-neutral-500 transition-colors hover:text-neutral-900"
          >
            ← Contractors AP
          </button>
        </div>
      </nav>

      {error && <ErrorNote>{error}</ErrorNote>}
      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        <Stat label="Committed" value={fmt(committed)} currency={currency} />
        <Stat
          label="Billed"
          value={fmt(billed)}
          currency={currency}
          hint="Against those commitments"
        />
        <Stat
          label="Still to come"
          value={fmt(sub(committed, billed))}
          currency={currency}
          hint="Committed, not yet billed"
        />
        <Stat
          label="Owed"
          value={fmt(outstanding)}
          currency={currency}
          hint="Billed, not yet paid"
          emphasis
        />
      </div>

      {contractor && canManagePortalAccess(role) && (
        <Panel title="Portal access">
          <ContractorPortalAccess
            slug={slug}
            contractorId={contractorId}
            email={contractor.email}
          />
        </Panel>
      )}

      <Panel title="Commitments">
        {rows.length === 0 ? (
          <Empty title="No commitments yet">
            Commit this contractor to a scope of work from a project's page.
          </Empty>
        ) : (
          <>
            <TableSearch
              value={commitmentsTable.query}
              onChange={commitmentsTable.setQuery}
              placeholder="Filter commitments…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="project"
                    active={commitmentsTable.sortKey === "project"}
                    dir={commitmentsTable.dir}
                    onSort={commitmentsTable.toggleSort}
                  >
                    Project
                  </Th>
                  <Th
                    sortKey="scope"
                    active={commitmentsTable.sortKey === "scope"}
                    dir={commitmentsTable.dir}
                    onSort={commitmentsTable.toggleSort}
                  >
                    Scope
                  </Th>
                  <Th
                    right
                    sortKey="retainage"
                    active={commitmentsTable.sortKey === "retainage"}
                    dir={commitmentsTable.dir}
                    onSort={commitmentsTable.toggleSort}
                  >
                    Retainage
                  </Th>
                  <Th
                    right
                    sortKey="value"
                    active={commitmentsTable.sortKey === "value"}
                    dir={commitmentsTable.dir}
                    onSort={commitmentsTable.toggleSort}
                  >
                    Committed
                  </Th>
                  <Th
                    right
                    sortKey="billed"
                    active={commitmentsTable.sortKey === "billed"}
                    dir={commitmentsTable.dir}
                    onSort={commitmentsTable.toggleSort}
                  >
                    Billed
                  </Th>
                  <Th
                    right
                    sortKey="stillToCome"
                    active={commitmentsTable.sortKey === "stillToCome"}
                    dir={commitmentsTable.dir}
                    onSort={commitmentsTable.toggleSort}
                  >
                    Still to come
                  </Th>
                  <Th
                    right
                    sortKey="outstanding"
                    active={commitmentsTable.sortKey === "outstanding"}
                    dir={commitmentsTable.dir}
                    onSort={commitmentsTable.toggleSort}
                  >
                    Owed
                  </Th>
                  <Th />
                </>
              }
            >
              {commitmentsTable.rows.map((cm) => (
                <tr
                  key={cm.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>{cm.projectName}</Td>
                  <Td muted>{cm.scope ?? "—"}</Td>
                  <Td right mono muted>
                    {formatPercent(cm.retainagePct)}
                  </Td>
                  <Td right mono>
                    {fmt(cm.contractValue)}
                    {cm.basis === "percent_of_contract" && (
                      <span className="ml-2 text-xs text-neutral-500">
                        {formatPercent(cm.percent ?? "0")} of contract
                      </span>
                    )}
                  </Td>
                  <Td right mono muted>
                    {fmt(cm.billed)}
                  </Td>
                  <Td right mono muted>
                    {fmt(sub(cm.contractValue, cm.billed))}
                  </Td>
                  <Td right mono>
                    {fmt(cm.outstanding)}
                  </Td>
                  <Td right>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        onClick={() => setOpenId(openId === cm.id ? null : cm.id)}
                      >
                        {openId === cm.id ? "Hide draws" : "Draws"}
                      </Button>
                      {role === "owner" && (
                        <DeleteButton
                          busy={del.deleting}
                          onConfirm={() => {
                            if (openId === cm.id) setOpenId(null);
                            del.remove("commitment", cm.id);
                          }}
                        />
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Panel>

      {openId && (
        <Panel title="What this contractor earns">
          <CommitmentEarnings
            key={openId}
            slug={slug}
            commitmentId={openId}
            busy={run.isPending}
            onError={setError}
            onChanged={refresh}
          />
        </Panel>
      )}

      {openId && (
        <Panel title="Draw schedule">
          {draws.isPending ? (
            <Loading />
          ) : !draws.data?.schedule.milestones.length ? (
            <ScheduleRowsForm
              intro="Split the commitment into the payments you'll make. Percentages resolve against the commitment value, and the last one absorbs the rounding so they always sum exactly."
              addLabel="Add draw"
              submitLabel="Save schedule"
              defaultRows={[
                { name: "First", percent: "50" },
                { name: "Progress", percent: "40" },
                { name: "Final", percent: "10" },
              ]}
              busy={run.isPending}
              onSubmit={(rows) =>
                run.mutate(() =>
                  unwrap(
                    api.api.c[":slug"].payables.commitments[":id"].draws.$put({
                      param: { slug, id: openId },
                      json: { draws: rows },
                    }),
                  ),
                )
              }
            />
          ) : (
            <>
              <TableSearch
                value={drawScheduleTable.query}
                onChange={drawScheduleTable.setQuery}
                placeholder="Filter draws…"
              />
              <Table
                head={
                  <>
                    <Th
                      sortKey="seq"
                      active={drawScheduleTable.sortKey === "seq"}
                      dir={drawScheduleTable.dir}
                      onSort={drawScheduleTable.toggleSort}
                    >
                      #
                    </Th>
                    <Th
                      sortKey="draw"
                      active={drawScheduleTable.sortKey === "draw"}
                      dir={drawScheduleTable.dir}
                      onSort={drawScheduleTable.toggleSort}
                    >
                      Draw
                    </Th>
                    <Th
                      sortKey="status"
                      active={drawScheduleTable.sortKey === "status"}
                      dir={drawScheduleTable.dir}
                      onSort={drawScheduleTable.toggleSort}
                    >
                      Status
                    </Th>
                    <Th
                      right
                      sortKey="amount"
                      active={drawScheduleTable.sortKey === "amount"}
                      dir={drawScheduleTable.dir}
                      onSort={drawScheduleTable.toggleSort}
                    >
                      Amount
                    </Th>
                    <Th
                      right
                      sortKey="outstanding"
                      active={drawScheduleTable.sortKey === "outstanding"}
                      dir={drawScheduleTable.dir}
                      onSort={drawScheduleTable.toggleSort}
                    >
                      Outstanding
                    </Th>
                    <Th />
                  </>
                }
              >
                {drawScheduleTable.rows.map((d) => {
                  const bill = draws.data?.settlements[d.id];
                  return (
                    <tr
                      key={d.id}
                      className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                    >
                      <Td mono muted>
                        {d.seq}
                      </Td>
                      <Td>{d.name}</Td>
                      <Td muted>{d.frozen ? "billed" : d.status}</Td>
                      <Td right mono>
                        {fmt(d.amount)}
                      </Td>
                      <Td right mono>
                        {bill ? fmt(bill.outstanding) : "—"}
                      </Td>
                      <Td right>
                        {!d.frozen && (
                          <Button
                            variant="ghost"
                            disabled={run.isPending}
                            onClick={() =>
                              run.mutate(() =>
                                unwrap(
                                  api.api.c[":slug"].payables.bills.$post({
                                    param: { slug },
                                    json: {
                                      commitmentId: openId,
                                      drawId: d.id,
                                      issueDate: new Date().toISOString().slice(0, 10),
                                    },
                                  }),
                                ),
                              )
                            }
                          >
                            Bill this draw
                          </Button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </Table>
            </>
          )}
        </Panel>
      )}

      <Panel title="Bills">
        {bills.isPending ? (
          <Loading />
        ) : !bills.data?.bills.length ? (
          <Empty title="No bills yet">Bill a draw above once a commitment is scheduled.</Empty>
        ) : (
          <>
            <TableSearch
              value={billsTable.query}
              onChange={billsTable.setQuery}
              placeholder="Filter bills…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="project"
                    active={billsTable.sortKey === "project"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Project
                  </Th>
                  <Th
                    sortKey="ref"
                    active={billsTable.sortKey === "ref"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Ref
                  </Th>
                  <Th
                    sortKey="due"
                    active={billsTable.sortKey === "due"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Due
                  </Th>
                  <Th
                    sortKey="status"
                    active={billsTable.sortKey === "status"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Status
                  </Th>
                  <Th
                    right
                    sortKey="retainage"
                    active={billsTable.sortKey === "retainage"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Retainage
                  </Th>
                  <Th
                    right
                    sortKey="total"
                    active={billsTable.sortKey === "total"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Total
                  </Th>
                  <Th
                    right
                    sortKey="paid"
                    active={billsTable.sortKey === "paid"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Paid
                  </Th>
                  <Th
                    right
                    sortKey="outstanding"
                    active={billsTable.sortKey === "outstanding"}
                    dir={billsTable.dir}
                    onSort={billsTable.toggleSort}
                  >
                    Owed
                  </Th>
                  <Th />
                </>
              }
            >
              {billsTable.rows.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td muted>{b.projectName}</Td>
                  <Td mono muted>
                    {b.number ?? "—"}
                  </Td>
                  <Td mono muted>
                    {b.dueDate}
                  </Td>
                  <Td>
                    {b.status}
                    {b.overdue && <span className="ml-2 text-xs text-red-600">overdue</span>}
                  </Td>
                  <Td right mono muted>
                    {fmt(b.retainageWithheld)}
                  </Td>
                  <Td right mono>
                    {fmt(b.total)}
                  </Td>
                  <Td right mono muted>
                    {fmt(b.amountPaid)}
                  </Td>
                  <Td right mono>
                    {fmt(b.outstanding)}
                  </Td>
                  <Td right>
                    {b.outstanding !== "0.00" && (
                      <Button
                        variant="ghost"
                        onClick={() => setSettlingBillId(settlingBillId === b.id ? null : b.id)}
                      >
                        {settlingBillId === b.id ? "Cancel" : "Record payment"}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
              {billsTable.rows
                .filter((b) => b.id === settlingBillId)
                .map((b) => (
                  <tr key={`${b.id}-form`}>
                    <td colSpan={9} className="bg-neutral-50 px-4 py-4">
                      <RecordPaymentForm
                        settlement={b}
                        currency={b.currency}
                        noun="bill"
                        dateLabel="Paid on"
                        busy={pay.isPending}
                        onSubmit={(p) =>
                          pay.mutate(() =>
                            unwrap(
                              api.api.c[":slug"].payables.payments.$post({
                                param: { slug },
                                json: {
                                  contractorId: b.contractorId,
                                  paidOn: p.on,
                                  amount: p.amount,
                                  currency: b.currency,
                                  ...(p.method ? { method: p.method } : {}),
                                  ...(p.reference ? { reference: p.reference } : {}),
                                  applications: [{ billId: b.id, amount: p.amount }],
                                },
                              }),
                            ),
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
            </Table>
          </>
        )}
      </Panel>

      <PriceBookPanel
        slug={slug}
        role={role}
        side="cost"
        contractorId={contractorId}
        currency={currency}
      />
    </>
  );
}
